import React, {FC, useEffect, useRef, useState} from 'react';
import {
    FreeMode,
    Navigation,
    Swiper,
    SwiperOptions,
    SwiperSlide,
    Thumbs,
} from '@/_application/core/components/ui/slider';
import {productPlaceholder} from '@/lib/placeholders';
import {ProductBadge, ProductImage, ProductVideo} from '__generated__/__types__';
import {SwiperClass} from 'swiper/react';
import {Thumbnails} from '@/_application/core/components/ui/thumb-carousel/thumbnails/thumbnails';
import {ChevronButton} from '@/_application/core/components/ui/thumb-carousel/chevron-button';
import {SlideIndicator} from '@/_application/core/components/ui/thumb-carousel/slide-indicator';
import {VideoSlide} from '@/_application/core/components/ui/thumb-carousel/slide-content/video-slide';
import {ImageSlide} from '@/_application/core/components/ui/thumb-carousel/slide-content/image-slide';
import {getAltText, getEmbeddedYoutubeUrl, isProductImage, isProductVideo} from '@/_application/helpers/media';
import {getCdnImageUrl} from '@/_application/helpers/cdn-image-resize';
import ProductBadgeComponent from '@/_application/modules/products/components/labels/product-badge';
import {FetchPriority} from '@/_application/core/types';
import {Product} from '@/_application/modules/products/model/product';
interface ThumbCarouselHandlerProps {
    gallery: (ProductImage | ProductVideo)[];
    badge?: ProductBadge;
    onImageClick?: () => void;
    onSlideChange: (slideIndex: number) => void;
    currentSlide: number;
    zoomMode?: boolean;
    showThumbNails?: boolean;
    onMainSwiper?: (swiper: SwiperClass) => void;
    hoverZoomMode?: boolean;
    product: Product;
}

const swiperParams: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
};

export const ThumbCarouselHandler: FC<ThumbCarouselHandlerProps> = ({
    gallery,
    onImageClick,
    onSlideChange,
    currentSlide,
    zoomMode = false,
    showThumbNails = false,
    onMainSwiper,
    badge,
    hoverZoomMode = false,
    product,
}) => {
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);
    const [firstThumb, setFirstThumb] = useState(0);
    const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);

    const handleSlideChange = (swiper: SwiperClass) => {
        onSlideChange(swiper.realIndex);
        setFirstThumb(swiper.realIndex);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!mainSwiper) return;

            if (e.key === 'ArrowLeft') {
                mainSwiper.slidePrev();
            } else if (e.key === 'ArrowRight') {
                mainSwiper.slideNext();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mainSwiper]);

    const prevClassName = zoomMode ? 'ltr:left-2 rtl:-right-4 rtl:md:-right-5' : 'ltr:left-2 ltr:md:-left-5';

    const nextClassName = zoomMode
        ? 'ltr:right-2 rtl:-left-4 rtl:md:-left-5'
        : 'ltr:right-2 ltr:md:-right-5 rtl:-left-4 rtl:md:-left-5';

    const handleImageClick = () => {
        return onImageClick && onImageClick();
    };

    const filteredGallery = gallery?.filter(
        item =>
            isProductImage(item) ||
            (isProductVideo(item) && (item as ProductVideo)?.video_content?.video_url?.split('v=')[1]),
    );
    const firstVisualIndex = filteredGallery.findIndex(item => isProductImage(item) || isProductVideo(item));
    return (
        <div className="flex h-full flex-col pt-4 justify-around">
            <div className="relative">
                <Swiper
                    id="productGallery"
                    onSwiper={swiper => {
                        setMainSwiper(swiper);
                        onMainSwiper?.(swiper);
                    }}
                    modules={[Navigation, Thumbs, FreeMode]}
                    navigation={{
                        prevEl: prevRef.current ?? null,
                        nextEl: nextRef.current ?? null,
                    }}
                    {...swiperParams}
                    observer={true}
                    initialSlide={currentSlide}
                    onSlideChange={handleSlideChange}
                    className="h-full">
                    {filteredGallery?.map((item, index) =>
                        isProductImage(item) ? (
                            <SwiperSlide
                                key={`product-gallery-${item.url}`}
                                className="m-auto flex! min-h-[375px] items-center justify-center selection:bg-transparent md:min-h-[512px]">
                                <ImageSlide
                                    variant="carousel"
                                    imageSrc={{
                                        url: zoomMode
                                            ? getCdnImageUrl(item?.url ?? productPlaceholder, 'original')
                                            : item?.url ?? productPlaceholder,
                                        alt: getAltText(item, index, product),
                                    }}
                                    zoomMode={zoomMode}
                                    hoverZoomMode={hoverZoomMode}
                                    onImageClick={handleImageClick}
                                    width={zoomMode ? 636 : 424}
                                    height={zoomMode ? 636 : 424}
                                    priority={index === firstVisualIndex}
                                    fetchPriority={index === firstVisualIndex ? FetchPriority.HIGH : FetchPriority.AUTO}
                                />
                            </SwiperSlide>
                        ) : (
                            <SwiperSlide
                                key={`product-video-${item?.url}`}
                                className="m-auto! flex! h-full! items-center justify-center">
                                <VideoSlide
                                    variant="video"
                                    width={zoomMode ? 636 : 424}
                                    height={zoomMode ? 636 : 424}
                                    embedUrl={getEmbeddedYoutubeUrl(item as ProductVideo)}
                                    priority={index === firstVisualIndex}
                                    imageSrc={{
                                        url: item.url ?? productPlaceholder,
                                        alt: getAltText(item, index, product),
                                    }}
                                    fetchPriority={index === firstVisualIndex ? FetchPriority.HIGH : FetchPriority.AUTO}
                                />
                            </SwiperSlide>
                        ),
                    )}
                </Swiper>
                {/* product badge */}
                {badge && firstThumb == 0 && (
                    <div className="absolute bottom-0 left-0 z-10 w-[50%]">
                        <ProductBadgeComponent badge={badge} isLCP={firstThumb === 0} />
                    </div>
                )}
                <ChevronButton
                    classNamePrev={prevClassName}
                    classNameNext={nextClassName}
                    prevRef={prevRef}
                    nextRef={nextRef}
                />

                {!zoomMode && <SlideIndicator currentSlide={currentSlide + 1} totalSlides={filteredGallery.length} />}
            </div>

            {showThumbNails && (
                <div className="p-6 md:block md:py-0">
                    <Thumbnails
                        badgeImage={badge?.image as string}
                        gallery={filteredGallery}
                        product={product}
                        currentSlide={currentSlide}
                        onThumbClick={index => mainSwiper?.slideTo(index)}
                    />
                </div>
            )}
        </div>
    );
};
